import Content from "../components/Content";
import PageTemplate from "../components/PageTemplate";
import { Accordion } from "radix-ui";
import { ChevronDownIcon } from '@radix-ui/react-icons';
import Divider from "../components/Divider";
import PageFade from "../motions/PageFade";
import { useEffect } from 'react';

const questions = {
  "General Information": [
    {
      q: "What time should we arrive on the wedding day?",
      a: "Doors will open at 3:30 PM, so please plan to arrive at the venue by 3:50 the latest, but no earlier than 3:30. The wedding ceremony will begin promptly at 4:00 PM."
    },
    {
      q: "What is the dress code?",
      a: "Black-tie or Formal (also known as Black-tie Optional). Tuxedos are encouraged, but a dark suit with a tie is also welcome. Floor-length gowns are encouraged, but a long elegant dress or a formal jumpsuit is also welcome. The bridal party will be wearing pastel pink, in case you’d like to avoid accidentally matching them."
    },
    {
      q: "What is the tea ceremony taking place on the wedding day?",
      a: "A tea ceremony is a traditional wedding ritual in many East Asian cultures that symbolizes respect, gratitude, and the union of two families. The couple serves tea to their elders as a gesture of appreciation and, in return, receives blessings and sometimes gifts."
    },
    {
      q: "Are there any other events happening besides the wedding?",
      a: (
        <>
          Yes! We are hosting a welcome party on Friday night before the wedding at
          Bonne Vie Cafe & Bistro (formerly known as Baby Shank restaurant). Please see our{" "}
          <a href="/events" className="underline hover:text-bronze">
            Events page
          </a>{" "}
          for more details!
        </>
      )
    }
  ],
  "Attendance & RSVP": [
    {
      q: "When is the RSVP deadline?",
      a: (
        <>
          Please RSVP by March 15th so we can finalize our guest count and prepare the
          correct amount of food. You can RSVP{" "}
          <a href="/rsvp" className="underline hover:text-bronze">
            here
          </a>
          .
        </>
      )
    },
    {
      q: "Can I bring my young children?",
      a: "Yes! We’re happy to welcome kids to our wedding. Your invitation includes your immediate young children, and their names will appear when you RSVP so you can indicate their attendance individually. If you don’t see their names listed, please reach out to us."
    },
    {
      q: "Can I bring a plus-one?",
      a: "We’re keeping our wedding intimate, with close friends and family. If your invitation includes a plus-one, it shall be indicated when you RSVP. Otherwise, please reach out to us and we’ll consider it if space allows."
    },
  ],
  "Venue": [
    {
      q: "Will the ceremony and reception be indoors or outdoors?",
      a: "The wedding ceremony and will take place outdoors in the garden, weather permitting. Cocktail hour and dinner may take place indoors or outdoors depending on weather. The tea ceremony and dancing will be held indoors."
    },
    {
      q: "Is there parking at the venue?",
      a: "There is a small parking lot, but parking is limited. If you are staying in DC, we recommend using a rideshare service like Uber or Lyft."
    },
    {
      q: "Will transportation be provided?",
      a: "No, we are not providing transportation, but the venue is easily accessible by rideshare."
    }
  ],
  "Food & Drink": [
    {
      q: "What kind of food will be served?",
      a: "Dinner will be a two-course plated meal plus cake and desserts. There will also be a variety of passed hors d’oeuvres during cocktail hour."
    },
    {
      q: "I have a dietary restriction. Will there be food options for me?",
      a: "We want everyone to enjoy their meal! Please let us know your dietary restrictions when you RSVP, and we will accommodate them."
    },
    {
      q: "Will there be an open bar?",
      a: "Yes! We will have a full-service open bar throughout the evening, as well as a specialty mocktail for those who do not drink alcohol."
    }
  ],
  "Contact": [
    {
      q: "What’s the best way to contact you if I have more questions?",
      a: (
        <>
          Before the wedding, feel free to reach out to us at{" "}
          <a
            href="mailto:annjerrygetmarried@gmail.com"
            className="underline hover:text-bronze"
          >
            annjerrygetmarried@gmail.com
          </a>
          .
        </>
      )
    }
  ]
};


function QandA() {
  console.log("QandA rendered");

  useEffect(() => {
    console.log("QandA mounted");

    return () => {
      console.log("QandA unmounted");
    };
  }, []);

  return (
    <PageFade>
      <PageTemplate title="Q&A" textColor="text-bronze" bgColor="bg-blush">
        <Content small={true}>
          <div className="mb-10 w-full text-bronze">
            <div className="block sm:hidden mb-4">
              Tap a question below to see the answer.
            </div>
            <div className="hidden sm:block">
              <DesktopView questions={questions} />
            </div>
            <div className="block sm:hidden">
              <MobileView questions={questions} />
            </div>
          </div>
        </Content>
      </PageTemplate>
    </PageFade>
  );
}

const DesktopView = ({ questions }) => {
  const keys = Object.keys(questions);
  return (
    <div className="flex flex-col">
      {keys.map((key, index) => (
        <div key={index} className="flex flex-col gap-y-6">
          <div className="text-bronze subheading text-2xl">
            {key.toUpperCase()}
          </div>
          <div className="flex flex-col gap-y-6">
            {questions[key].map((q, index) => (
              <div key={index}>
                <p className="text-bronze p-2">{q.q}</p>
                <p className="text-customGray">{q.a}</p>
              </div>
            ))}
          </div>
          {index !== keys.length - 1 && (
            <Divider className="border-bronze border-t w-1/2 mx-auto my-10" />
          )}
        </div>
      ))}
    </div>
  )
}

const MobileView = ({ questions }) => {
  return (
    <>
      {Object.keys(questions).map((key, index) => (
        <div key={index}>
          <div className="subheading p-3">
            {key.toUpperCase()}
          </div>
          <QuestionsAccordion questions={questions[key]} />
        </div>
      ))}
    </>
  )
}

const QuestionsAccordion = ({ questions }) => (
  <div className="w-full mx-auto">
    <Accordion.Root type="multiple" className="w-full border border-rose bg">
      {questions.map((key, index) => (
        <Accordion.Item key={index} value={index + 1} className="w-full border border-rose">
          <Accordion.Header>
            <Accordion.Trigger className="w-full text-customGray bg-blush hover:text-white hover:bg-rose ">
              <div className="p-3 group flex justify-between items-center">
                <div className="text-center flex-grow">{key.q}</div>
                <ChevronDownIcon
                  className="transition-transform duration-200 group-aria-expanded:rotate-180"
                  aria-hidden
                />
              </div>
            </Accordion.Trigger>
          </Accordion.Header>
          <Accordion.Content className="AccordionContent border-t border-rose">
            <div className="p-4">
              {key.a}
            </div>
          </Accordion.Content>
        </Accordion.Item>
      ))}
    </Accordion.Root>
  </div>
);

export default QandA;
